import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { getFileContent } from "../../api/Design/fileApi";
import { renderPdfThumbnailFromBase64 } from "../../utils/pdfThumbnail";
import ZoomableFilePreviewDialog from "./ZoomableFilePreviewDialog";

function base64ToBlob(base64Data, contentType = "") {
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = [];

  for (let sliceIndex = 0; sliceIndex < slicesCount; sliceIndex++) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);
    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; offset++, i++) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays.push(new Uint8Array(bytes));
  }
  return new Blob(byteArrays, { type: contentType });
}

const thumbBoxSx = {
  padding: "0px 0em",
  width: "5em",
  cursor: "pointer",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const thumbImgSx = {
  width: "80%",
  maxWidth: "80px",
  borderRadius: "4px",
  border: "1px solid #e0e0e0",
  objectFit: "cover",
};

export default function FileThumbnails({ filePath, cover = false }) {
  const [currentFile, setCurrentFile] = useState(null);
  const [error, setError] = useState(null);
  const [pdfThumbSrc, setPdfThumbSrc] = useState(null);
  const [pdfThumbError, setPdfThumbError] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (filePath) {
      fetchFile(filePath);
    }
  }, [filePath]);

  useEffect(() => {
    if (!currentFile || currentFile.mimeType !== "application/pdf") return;

    let cancelled = false;
    setPdfThumbSrc(null);
    setPdfThumbError(false);

    renderPdfThumbnailFromBase64(currentFile.data, 120)
      .then((src) => {
        if (!cancelled) setPdfThumbSrc(src);
      })
      .catch((err) => {
        console.error("Error generating PDF thumbnail:", err);
        if (!cancelled) setPdfThumbError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [currentFile]);

  const pdfUrl = useMemo(() => {
    if (!currentFile || currentFile.mimeType !== "application/pdf") return null;
    const pdfBlob = base64ToBlob(currentFile.data, currentFile.mimeType);
    return URL.createObjectURL(pdfBlob);
  }, [currentFile]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const fetchFile = async (path) => {
    try {
      const response = await getFileContent(path);
      if (response && response.data) {
        setCurrentFile(response.data);
      } else {
        console.error("Respuesta inesperada:", response);
      }
    } catch (err) {
      setError("Error al obtener el archivo.");
      console.error(err);
    }
  };

  const handleThumbnailClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const fileName = String(filePath || "")
    .split("/")
    .pop();

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!currentFile) {
    return (
      <Skeleton
        variant="rectangular"
        width={cover ? "100%" : 56}
        height={cover ? "100%" : 56}
        sx={{ borderRadius: 1 }}
      />
    );
  }

  const { mimeType, data } = currentFile;

  if (mimeType === "application/pdf") {
    return (
      <>
        <Box
          sx={thumbBoxSx}
          onClick={handleThumbnailClick}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {pdfThumbSrc ? (
            <Box component="img" src={pdfThumbSrc} alt="PDF" sx={thumbImgSx} />
          ) : pdfThumbError ? (
            <Box
              sx={{
                ...thumbImgSx,
                width: 56,
                height: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#f5f5f5",
                fontSize: "0.65rem",
                fontWeight: 700,
                color: "#606060",
              }}
            >
              PDF
            </Box>
          ) : (
            <Skeleton
              variant="rectangular"
              width={56}
              height={56}
              sx={{ borderRadius: 1 }}
            />
          )}
        </Box>

        <ZoomableFilePreviewDialog
          open={previewOpen}
          onClose={handlePreviewClose}
          title={fileName}
          kind="pdf"
          src={pdfUrl}
        />
      </>
    );
  }

  if (mimeType && mimeType.includes("image")) {
    const imgSrc = `data:${mimeType};base64,${data}`;

    if (cover) {
      return (
        <Box
          component="img"
          src={imgSrc}
          alt=""
          draggable={false}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            pointerEvents: "none",
          }}
        />
      );
    }

    return (
      <>
        <Box
          sx={thumbBoxSx}
          onClick={handleThumbnailClick}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Box component="img" src={imgSrc} alt="Imagen" sx={thumbImgSx} />
        </Box>

        <ZoomableFilePreviewDialog
          open={previewOpen}
          onClose={handlePreviewClose}
          title={fileName}
          kind="image"
          src={imgSrc}
        />
      </>
    );
  }

  const extension = mimeType.split("/")[1]?.toLowerCase() || "archivo";

  let label = "Archivo";
  if (mimeType.includes("zip")) label = "ZIP";
  else if (mimeType.includes("word")) label = "DOCX";
  else if (mimeType.includes("excel")) label = "XLSX";
  else if (mimeType.includes("csv")) label = "CSV";
  else if (mimeType.includes("text")) label = "TXT";
  else if (mimeType.includes("ms-powerpoint") || extension === "pptx")
    label = "PPTX";

  return (
    <Box
      sx={{
        margin: " 0px 0em 0px 1.4em",
        cursor: "pointer",
        width: "6.6em",
        height: "5.5em",
        background: "#eeeeee",
        borderRadius: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.75em",
        fontWeight: "bold",
        color: "#606060",
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {label}
    </Box>
  );
}
